import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './OperatorInvoices.css';
import { formatCurrency, numberToWordsCurrency, getCountryConfig } from '../utils/formatters';
import CreateInvoicePage from './CreateInvoicePage';

const API = '/api/invoices';
const CLIENTS_API = '/api/clients';
const ROUTES_API = '/api/routes';
const MASTER_DATA_API = '/api/master-data';
const UPLOAD_URL = '/api/uploads/';

/* ── Inline SVG Icons ── */
const Icon = ({ d, size = 16, strokeWidth = 1.75 }) => (
    <svg
        width={size} height={size} viewBox="0 0 24 24"
        fill="none" stroke="currentColor"
        strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round"
    >
        {Array.isArray(d) ? d.map((p, i) => <path key={i} d={p} />) : <path d={d} />}
    </svg>
);

const icons = {
    plus: ['M12 5v14', 'M5 12h14'],
    search: ['M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16z', 'M21 21l-4.35-4.35'],
    edit: ['M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7', 'M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z'],
    trash: ['M3 6h18', 'M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2'],
    x: ['M18 6 6 18', 'M6 6l12 12'],
    check: ['M20 6 9 17l-5-5'],
    eye: ['M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z', 'M12 9a3 3 0 1 0 0 6 3 3 0 0 0 0-6z'],
    wallet: ['M20 12V8H6a2 2 0 0 1-2-2V5', 'M4 19v-2a2 2 0 0 1 2-2h14v3a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2zm16-4V8'],
    file: ['M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z', 'M14 2v6h6'],
    dollar: ['M12 1v22', 'M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6'],
    alert: ['M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z', 'M12 9v4', 'M12 17h.01'],
};

export default function OperatorInvoices({ user: propUser, operatorDetails }) {
    const [invoices, setInvoices] = useState([]);
    const [clients, setClients] = useState([]);
    const [routes, setRoutes] = useState([]);
    const [paymentMethods, setPaymentMethods] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [modalOpen, setModalOpen] = useState(false);
    const [viewModalOpen, setViewModalOpen] = useState(false);
    const [selectedInvoiceToView, setSelectedInvoiceToView] = useState(null);
    const [statusModalOpen, setStatusModalOpen] = useState(false);
    const [editItem, setEditItem] = useState(null);
    const [selectedInvoice, setSelectedInvoice] = useState(null);
    const [createPageOpen, setCreatePageOpen] = useState(false);
    const [editItemForPage, setEditItemForPage] = useState(null);

    const user = propUser || JSON.parse(localStorage.getItem('ops-user'));
    const operatorId = user?.operatorId || (user?.userType === 'operator' ? user._id : 'all');
    const country = operatorDetails?.country;

    // Form State for Invoice Creation/Edit
    const [formData, setFormData] = useState({
        clientId: '',
        invoiceNumber: '',
        issueDate: new Date().toISOString().split('T')[0],
        dueDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        items: [{ routeId: '', routeName: '', tripsMonthly: 0, tripsDaily: 0, amountPerTrip: 0, totalAmount: 0 }],
        additionalAmount: 0,
        vatPercentage: 0,
        subject: '',
        description: '',
        notes: '',
        signatureName: 'Authorized Signatory'
    });


    // Form State for Status Update
    const [paymentData, setPaymentData] = useState({
        status: 'Paid',
        paidDate: new Date().toISOString().split('T')[0],
        paymentMethod: '',
        paymentNotes: '',
    });
    const [paymentFile, setPaymentFile] = useState(null);

    useEffect(() => {
        if (operatorId && operatorId !== 'all') fetchData();
    }, [operatorId]);



    const fetchData = async () => {
        if (!operatorId) return;
        setLoading(true);
        try {
            const [invRes, clientRes, routeRes, masterRes] = await Promise.all([
                axios.get(`${API}/${operatorId}`),
                axios.get(`${CLIENTS_API}/${operatorId}`),
                axios.get(`${ROUTES_API}/${operatorId}`),
                axios.get(MASTER_DATA_API)
            ]);
            setInvoices(invRes.data);
            setClients(clientRes.data);
            setRoutes(routeRes.data);

            // Extract payment methods from master data
            const methods = masterRes.data.filter(item => item.category === 'payment-methods');
            setPaymentMethods(methods);
        } catch (err) {
            console.error("Fetch error:", err);
        } finally {
            setLoading(false);
        }
    };


    const handleViewInvoice = (inv) => {
        setSelectedInvoiceToView(inv);
        setViewModalOpen(true);
    };

    const handlePrint = () => {
        const content = document.getElementById('printable-invoice');
        const printWindow = window.open('', '', 'width=900,height=650');
        printWindow.document.write('<html><head><title>Print Invoice</title>');
        printWindow.document.write('<style>body { font-family: sans-serif; padding: 20px;} .invoice-header { display: flex; justify-content: space-between; margin-bottom: 40px; } .invoice-table { width: 100%; border-collapse: collapse; margin-top: 20px; } .invoice-table th, .invoice-table td { border: 1px solid #ccc; padding: 10px; text-align: left; } .totals { width: 300px; margin-left: auto; margin-top: 20px; }</style>');
        printWindow.document.write('</head><body>');
        printWindow.document.write(content.innerHTML);
        printWindow.document.write('</body></html>');
        printWindow.document.close();
        printWindow.focus();
        setTimeout(() => { printWindow.print(); printWindow.close(); }, 250);
    };


    const handleOpenModal = (item = null) => {
        setEditItemForPage(item || null);
        setCreatePageOpen(true);
    };

    const handleOpenStatusModal = (inv) => {
        setSelectedInvoice(inv);
        setPaymentData({
            status: inv.status === 'Paid' ? 'Paid' : 'Paid',
            paidDate: inv.paidDate ? new Date(inv.paidDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
            paymentMethod: inv.paymentMethod || '',
            paymentNotes: inv.paymentNotes || '',
        });
        setPaymentFile(null);
        setStatusModalOpen(true);
    };


    const handleAddItem = () => {
        setFormData(prev => ({
            ...prev,
            items: [...prev.items, { routeId: '', routeName: '', tripsMonthly: 0, tripsDaily: 0, amountPerTrip: 0, totalAmount: 0 }]
        }));
    };

    const handleRemoveItem = (index) => {
        const newItems = [...formData.items];
        newItems.splice(index, 1);
        setFormData(prev => ({ ...prev, items: newItems }));
    };

    const handleItemChange = (index, field, value) => {
        const newItems = [...formData.items];
        const item = { ...newItems[index] };

        if (field === 'routeId') {
            const selectedRoute = routes.find(r => r._id === value);
            item.routeId = value;
            item.routeName = selectedRoute ? selectedRoute.name : '';
        } else {
            item[field] = value;
        }

        const totalTrips = (Number(item.tripsMonthly) || 0) + (Number(item.tripsDaily) || 0);
        item.totalAmount = totalTrips * (Number(item.amountPerTrip) || 0);

        newItems[index] = item;
        setFormData(prev => ({ ...prev, items: newItems }));
    };

    const calculateTotals = () => {
        const tripsSubtotal = formData.items.reduce((sum, item) => sum + (Number(item.totalAmount) || 0), 0);
        const subtotal = tripsSubtotal + (Number(formData.additionalAmount) || 0);
        const vatAmount = subtotal * (Number(formData.vatPercentage) / 100);
        const totalAmount = subtotal + vatAmount;

        return { tripsSubtotal, subtotal, vatAmount, totalAmount };
    };

    const { tripsSubtotal, subtotal, vatAmount, totalAmount } = calculateTotals();

    const handleSave = async (e) => {
        e.preventDefault();
        const data = {
            ...formData,
            operatorId,
            tripsSubtotal,
            subtotal,
            vatAmount,
            totalAmount
        };

        try {
            if (editItem) {
                await axios.put(`${API}/${editItem._id}`, data);
            } else {
                await axios.post(API, data);
            }
            setModalOpen(false);
            fetchData();
        } catch (err) {
            console.error("Save error:", err);
            alert("Failed to save invoice");
        }
    };

    const handleUpdateStatus = async (e) => {
        e.preventDefault();
        const data = new FormData();
        data.append('status', paymentData.status);
        if (paymentData.status === 'Paid') {
            data.append('paidDate', paymentData.paidDate);
            data.append('paymentMethod', paymentData.paymentMethod);
            data.append('paymentNotes', paymentData.paymentNotes);
            if (paymentFile) {
                data.append('paymentAttachment', paymentFile);
            }
        } else {
            data.append('paidDate', '');
            data.append('paymentMethod', '');
            data.append('paymentNotes', '');
        }

        try {
            await axios.put(`${API}/${selectedInvoice._id}`, data);
            setStatusModalOpen(false);
            fetchData();
        } catch (err) {
            console.error("Status update error:", err);
            alert("Failed to update status");
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Delete this invoice?")) return;
        try {
            await axios.delete(`${API}/${id}`);
            fetchData();
        } catch (err) {
            console.error("Delete error:", err);
        }
    };

    const filteredInvoices = invoices.filter(inv =>
        inv.invoiceNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        inv.clientId?.companyName?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const pendingInvoicesCount = invoices.filter(inv => inv.status !== 'Paid').length;
    const paidInvoicesCount = invoices.filter(inv => inv.status === 'Paid').length;
    const totalRevenue = invoices.filter(inv => inv.status === 'Paid').reduce((sum, inv) => sum + (inv.totalAmount || 0), 0);

    // If create/edit page is open, render it directly inside the operator layout (no fullscreen takeover)
    if (createPageOpen) {
        return (
            <div className="operator-page" style={{ padding: 0 }}>
                <CreateInvoicePage
                    user={propUser}
                    operatorDetails={operatorDetails}
                    editItem={editItemForPage}
                    onBack={() => setCreatePageOpen(false)}
                    onSaved={() => { setCreatePageOpen(false); fetchData(); }}
                />
            </div>
        );
    }

    if (viewModalOpen && selectedInvoiceToView) {
        return (
            <div style={{ background: '#f8fafc', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
                <div className="no-print" style={{ padding: '12px 24px', background: 'white', borderBottom: '1px solid #eef2f6', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, zIndex: 100 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
                        <button onClick={() => setViewModalOpen(false)} style={{ background: 'transparent', border: 'none', color: '#64748b', display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                            <Icon d={icons.chevronDown} size={16} style={{ transform: 'rotate(90deg)' }} /> Back
                        </button>
                        <div style={{ display: 'flex', gap: 0, borderBottom: '2px solid transparent' }}>
                            <button style={{ padding: '12px 24px', border: 'none', background: 'transparent', fontSize: 13, fontWeight: 700, cursor: 'pointer', borderBottom: '2px solid #0f172a', color: '#0f172a' }}>Invoice</button>
                            <button style={{ padding: '12px 24px', border: 'none', background: 'transparent', fontSize: 13, fontWeight: 700, cursor: 'pointer', borderBottom: '2px solid transparent', color: '#94a3b8' }}>Activity Log</button>
                        </div>
                    </div>
                    <div style={{ display: 'flex', gap: 12 }}>
                        <button onClick={() => { setViewModalOpen(false); handleOpenModal(selectedInvoiceToView); }} style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 16px', fontSize: 12, fontWeight: 700, color: '#334155', display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                            <Icon d={icons.edit} size={14} /> Edit
                        </button>
                        <button onClick={handlePrint} style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 16px', fontSize: 12, fontWeight: 700, color: '#334155', display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                            <Icon d={icons.analytics} size={14} /> Print
                        </button>
                        <button onClick={handlePrint} style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 16px', fontSize: 12, fontWeight: 700, color: '#334155', display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                            <Icon d={icons.file} size={14} /> Download PDF
                        </button>
                        <button style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 16px', fontSize: 12, fontWeight: 700, color: '#334155', display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                            More <Icon d={icons.chevronDown} size={12} />
                        </button>
                    </div>
                </div>

                <div style={{ flex: 1, padding: '60px 24px', background: '#f8fafc', display: 'flex', justifyContent: 'center' }}>
                    <div id="printable-invoice" style={{ background: '#fff', width: '210mm', minHeight: '297mm', padding: '60px 50px', boxSizing: 'border-box', fontFamily: "'Inter', sans-serif", color: '#1e293b' }}>
                        {/* Top Header */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 40 }}>
                            <div style={{ flex: 1 }}>
                                {operatorDetails?.logo ? (
                                    <img src={`${UPLOAD_URL}${operatorDetails.logo}`} alt="Logo" style={{ maxHeight: '60px', maxWidth: '200px', marginBottom: '12px', objectFit: 'contain' }} />
                                ) : (
                                    <div style={{ fontSize: '28px', fontWeight: '900', color: '#0f172a', letterSpacing: '-0.5px', marginBottom: '8px' }}>
                                        {operatorDetails?.companyName || 'Your Company'}
                                    </div>
                                )}
                                <div style={{ fontSize: '12px', color: '#64748b', lineHeight: '1.6' }}>{operatorDetails?.address || ''}</div>
                                <div style={{ fontSize: '12px', color: '#64748b', lineHeight: '1.6' }}>{operatorDetails?.email || ''}</div>
                            </div>
                            <div style={{ textAlign: 'right', background: '#f8fafc', padding: '20px 24px', borderRadius: '12px', border: '1px solid #f1f5f9', minWidth: '220px' }}>
                                <div style={{ fontSize: '32px', fontWeight: '900', color: '#0f172a', letterSpacing: '-0.5px', marginBottom: '12px' }}>INVOICE</div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '24px', fontSize: '12px', color: '#475569', marginBottom: '6px' }}>
                                    <span style={{ fontWeight: '600' }}>Ref No:</span> <span>{selectedInvoiceToView.invoiceNumber || 'INV-XXXXX'}</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '24px', fontSize: '12px', color: '#475569', marginBottom: '6px' }}>
                                    <span style={{ fontWeight: '600' }}>Issued:</span> <span>{new Date(selectedInvoiceToView.issueDate).toLocaleDateString('en-GB')}</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '24px', fontSize: '12px', color: '#475569' }}>
                                    <span style={{ fontWeight: '600' }}>Due:</span> <span>{selectedInvoiceToView.dueDate ? new Date(selectedInvoiceToView.dueDate).toLocaleDateString('en-GB') : '—'}</span>
                                </div>
                            </div>
                        </div>

                        {/* Bill To & Subject Section */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '32px', paddingBottom: '20px', borderBottom: '1px solid #f1f5f9' }}>
                            <div>
                                <div style={{ fontSize: '10px', fontWeight: '800', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '8px' }}>Billed To</div>
                                {selectedInvoiceToView.clientId ? (
                                    <>
                                        <div style={{ fontWeight: '800', color: '#0f172a', fontSize: '18px', marginBottom: '4px' }}>{selectedInvoiceToView.clientId.companyName}</div>
                                        <div style={{ color: '#64748b', fontSize: '13px', lineHeight: '1.5' }}>{selectedInvoiceToView.clientId.address || ''}</div>
                                        <div style={{ color: '#64748b', fontSize: '13px', lineHeight: '1.5' }}>{selectedInvoiceToView.clientId.contactEmail || ''}</div>
                                    </>
                                ) : (
                                    <div style={{ color: '#94a3b8', fontStyle: 'italic', fontSize: '13px' }}>Client not specified</div>
                                )}
                            </div>
                        </div>

                        {selectedInvoiceToView.subject && (
                            <div style={{ marginBottom: '32px' }}>
                                <h4 style={{ margin: '0 0 8px 0', fontSize: '16px', fontWeight: '800', color: '#0f172a' }}>{selectedInvoiceToView.subject}</h4>
                                {selectedInvoiceToView.description && (
                                    <div style={{ fontSize: '13px', color: '#475569', lineHeight: '1.6' }} dangerouslySetInnerHTML={{ __html: selectedInvoiceToView.description }}></div>
                                )}
                            </div>
                        )}

                        {/* Items table */}
                        {selectedInvoiceToView.items && selectedInvoiceToView.items.length > 0 && (
                            <div style={{ marginBottom: '40px' }}>
                                <div style={{ display: 'grid', gridTemplateColumns: '30px 1fr 80px 100px 110px', gap: '12px', padding: '12px 16px', borderBottom: '2px solid #0f172a', alignItems: 'center' }}>
                                    <span style={{ fontSize: '11px', fontWeight: '800', color: '#0f172a', letterSpacing: '0.05em' }}>#</span>
                                    <span style={{ fontSize: '11px', fontWeight: '800', color: '#0f172a', letterSpacing: '0.05em' }}>DESCRIPTION</span>
                                    <span style={{ fontSize: '11px', fontWeight: '800', color: '#0f172a', letterSpacing: '0.05em', textAlign: 'right' }}>{selectedInvoiceToView.billingType === 'Ad-hoc' ? 'PASSENGERS' : 'QTY'}</span>
                                    <span style={{ fontSize: '11px', fontWeight: '800', color: '#0f172a', letterSpacing: '0.05em', textAlign: 'right' }}>RATE</span>
                                    <span style={{ fontSize: '11px', fontWeight: '800', color: '#0f172a', letterSpacing: '0.05em', textAlign: 'right' }}>AMOUNT</span>
                                </div>
                                {selectedInvoiceToView.items.map((item, i) => {
                                    const qty = selectedInvoiceToView.billingType === 'Monthly' ? 1 : (item.totalTrips || ((item.tripsMonthly || 0) + (item.tripsDaily || 0)) || 1);
                                    return (
                                        <div key={i} style={{ display: 'grid', gridTemplateColumns: '30px 1fr 80px 100px 110px', gap: '12px', padding: '16px', borderBottom: '1px solid #f1f5f9', alignItems: 'flex-start' }}>
                                            <span style={{ fontSize: '13px', fontWeight: '600', color: '#64748b', paddingTop: '2px' }}>{i + 1}</span>
                                            <div>
                                                <div style={{ fontSize: '14px', fontWeight: '700', color: '#0f172a' }}>{item.itemName || 'Transportation'}</div>
                                                <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>{item.routeId?.name || item.routeName || ''}</div>
                                            </div>
                                            <span style={{ fontSize: '13px', color: '#475569', textAlign: 'right', fontWeight: '600' }}>{Number(qty).toFixed(2)}</span>
                                            <span style={{ fontSize: '13px', color: '#475569', textAlign: 'right', fontWeight: '600' }}>{Number(item.amountPerTrip || 0).toFixed(3)}</span>
                                            <span style={{ fontSize: '14px', fontWeight: '800', color: '#0f172a', textAlign: 'right' }}>{Number(item.totalAmount || 0).toFixed(3)}</span>
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {/* Totals Section */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '40px' }}>
                            <div style={{ flex: 1, paddingRight: '40px' }}>
                                <h4 style={{ fontSize: '11px', fontWeight: '800', color: '#64748b', margin: '0 0 8px 0', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Total Amount In Words:</h4>
                                <div style={{ fontSize: '13px', fontWeight: '700', color: '#0f172a', textTransform: 'uppercase', lineHeight: '1.5' }}>
                                    {numberToWordsCurrency(selectedInvoiceToView.totalAmount, country).toUpperCase()}
                                </div>
                            </div>
                            <div style={{ width: '300px' }}>
                                {[['Subtotal', selectedInvoiceToView.subtotal || selectedInvoiceToView.totalAmount], ['Additional', Number(selectedInvoiceToView.additionalAmount) || 0], [`VAT (${selectedInvoiceToView.vatPercentage || 0}%)`, selectedInvoiceToView.vatAmount || 0]].map(([lbl, val]) => (
                                    <div key={lbl} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: '#475569', padding: '8px 0', borderBottom: '1px solid #f8fafc' }}>
                                        <span style={{ fontWeight: '600' }}>{lbl}</span><span style={{ fontWeight: '700' }}>{formatCurrency(val, country)}</span>
                                    </div>
                                ))}
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '16px', padding: '16px', background: '#0f172a', color: '#fff', borderRadius: '12px' }}>
                                    <span style={{ fontSize: '14px', fontWeight: '700' }}>TOTAL DUE</span>
                                    <span style={{ fontSize: '20px', fontWeight: '900' }}>{formatCurrency(selectedInvoiceToView.totalAmount, country)}</span>
                                </div>
                            </div>
                        </div>

                        {/* Footer notes & Signature */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginTop: '60px' }}>
                            <div style={{ flex: 1, paddingRight: '40px' }}>
                                {selectedInvoiceToView.notes && (
                                    <div style={{ padding: '20px', background: '#f8fafc', borderRadius: '12px', borderLeft: '4px solid #cbd5e1' }}>
                                        <div style={{ fontSize: '10px', fontWeight: '800', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' }}>Notes & Terms</div>
                                        <div style={{ fontSize: '12px', color: '#475569', lineHeight: '1.6' }} dangerouslySetInnerHTML={{ __html: selectedInvoiceToView.notes }}></div>
                                    </div>
                                )}
                            </div>
                            <div style={{ textAlign: 'center', width: '220px' }}>
                                <div style={{ borderBottom: '2px solid #0f172a', marginBottom: '12px', height: '40px' }}></div>
                                <strong style={{ fontSize: '14px', color: '#0f172a' }}>{selectedInvoiceToView.signatureName || 'Authorized Signatory'}</strong>
                                <div style={{ fontSize: '11px', color: '#64748b', marginTop: '4px' }}>Signature</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    /* ── Derived KPI values ── */
    const totalInvoiced = invoices.reduce((s, inv) => s + (inv.totalAmount || 0), 0);
    const totalPaid = invoices.filter(inv => inv.status === 'Paid').reduce((s, inv) => s + (inv.totalAmount || 0), 0);
    const totalOutstanding = totalInvoiced - totalPaid;

    return (
        <div className="operator-page">
            <div className="op-breadcrumb">
                <span className="home-icon">🏠</span>
                <span className="sep">›</span>
                <span>Finance</span>
                <span className="sep">›</span>
                <span className="current">Invoice</span>
            </div>

            <div className="op-header">
                <div className="op-header-left">
                    <h1 className="op-title">Invoice Management</h1>
                    <p className="op-subtitle">Create and track client invoices</p>
                </div>
                <div className="op-header-actions">
                    <button className="op-btn op-btn-solid-primary" onClick={() => handleOpenModal()}>
                        <Icon d={icons.plus} size={15} />
                        New Invoice
                    </button>
                </div>
            </div>

            {/* Premium KPI Cards */}
            <div className="op-kpi-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)', gap: 18, marginBottom: 26 }}>
                <div style={{ background: 'white', borderRadius: 18, padding: '22px 24px', border: '1px solid #e2e8f0', borderTop: '4px solid #2563eb', boxShadow: '0 4px 12px rgba(0,0,0,0.04)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                        <div style={{ fontSize: 11, fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Total Invoiced</div>
                        <div style={{ width: 38, height: 38, borderRadius: 10, background: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Icon d={icons.file} size={18} strokeWidth={2} />
                        </div>
                    </div>
                    <div style={{ fontSize: 28, fontWeight: 800, color: '#0f172a', letterSpacing: '-0.03em', lineHeight: 1 }}>{formatCurrency(totalInvoiced, country)}</div>
                    <div style={{ fontSize: 12, color: '#64748b', marginTop: 8, fontWeight: 500 }}>{invoices.length} invoice{invoices.length !== 1 ? 's' : ''}</div>
                </div>

                <div style={{ background: 'white', borderRadius: 18, padding: '22px 24px', border: '1px solid #e2e8f0', borderTop: '4px solid #1d4ed8', boxShadow: '0 4px 12px rgba(0,0,0,0.04)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                        <div style={{ fontSize: 11, fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Total Paid</div>
                        <div style={{ width: 38, height: 38, borderRadius: 10, background: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Icon d={icons.dollar} size={18} strokeWidth={2} />
                        </div>
                    </div>
                    <div style={{ fontSize: 28, fontWeight: 800, color: '#0f172a', letterSpacing: '-0.03em', lineHeight: 1 }}>{formatCurrency(totalPaid, country)}</div>
                    <div style={{ fontSize: 12, color: '#64748b', marginTop: 8, fontWeight: 500 }}>{paidInvoicesCount} paid invoice{paidInvoicesCount !== 1 ? 's' : ''}</div>
                </div>

                <div style={{ background: 'white', borderRadius: 18, padding: '22px 24px', border: '1px solid #e2e8f0', borderTop: '4px solid #0284c7', boxShadow: '0 4px 12px rgba(0,0,0,0.04)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                        <div style={{ fontSize: 11, fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Outstanding</div>
                        <div style={{ width: 38, height: 38, borderRadius: 10, background: '#f0f9ff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Icon d={icons.alert} size={18} strokeWidth={2} />
                        </div>
                    </div>
                    <div style={{ fontSize: 28, fontWeight: 800, color: '#0f172a', letterSpacing: '-0.03em', lineHeight: 1 }}>{formatCurrency(totalOutstanding, country)}</div>
                    <div style={{ fontSize: 12, color: '#64748b', marginTop: 8, fontWeight: 500 }}>{pendingInvoicesCount} pending invoice{pendingInvoicesCount !== 1 ? 's' : ''}</div>
                </div>
            </div>

            {/* Search Bar */}
            <div className="op-filters-card" style={{ marginBottom: '20px' }}>
                <div className="op-filters-grid" style={{ gridTemplateColumns: '1fr' }}>
                    <div className="op-filter-field">
                        <label>Search</label>
                        <div className="op-filter-input-wrapper">
                            <span className="op-filter-icon"><Icon d={icons.search} size={14} /></span>
                            <input
                                type="text"
                                className="op-filter-input"
                                placeholder="Search by Invoice # or Client Name"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Invoice Records Table */}
            <div className="op-records-card">
                <div className="op-records-header">
                    <h2 className="op-records-title">All Invoices ({filteredInvoices.length} of {invoices.length} records)</h2>
                </div>
                <div className="op-table-wrapper">
                    <table className="op-records-table">
                        <thead>
                            <tr>
                                <th>Invoice #</th>
                                <th>Client</th>
                                <th>Date</th>
                                <th>Due Date</th>
                                <th>Amount</th>
                                <th>Status</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan="7" style={{ textAlign: 'center', padding: '50px', color: '#94a3b8' }}>Loading invoices…</td></tr>
                            ) : filteredInvoices.length === 0 ? (
                                <tr><td colSpan="7" style={{ textAlign: 'center', padding: '50px', color: '#94a3b8' }}>No invoices found</td></tr>
                            ) : filteredInvoices.map((inv) => (
                                <tr key={inv._id}>
                                    <td style={{ fontWeight: 700, color: '#1e293b' }}>{inv.invoiceNumber}</td>
                                    <td style={{ fontWeight: 500 }}>{inv.clientId?.companyName || 'Unknown'}</td>
                                    <td>{new Date(inv.issueDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</td>
                                    <td>{new Date(inv.dueDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</td>
                                    <td style={{ fontWeight: 700, color: '#0f172a' }}>{formatCurrency(inv.totalAmount, country)}</td>
                                    <td>
                                        <span className={`status-badge ${inv.status.toLowerCase()}`}>{inv.status}</span>
                                    </td>
                                    <td className="inv-actions">
                                        <button title="View Invoice" className="op-action-btn" onClick={() => handleViewInvoice(inv)}>
                                            <Icon d={icons.eye} size={13} />
                                        </button>
                                        <button className="op-action-btn" title="Update Payment Status" onClick={() => handleOpenStatusModal(inv)}>
                                            <Icon d={icons.wallet} size={13} />
                                        </button>
                                        <button title="Edit" className="op-action-btn" onClick={() => handleOpenModal(inv)}>
                                            <Icon d={icons.edit} size={13} />
                                        </button>
                                        {inv.paymentAttachment && (
                                            <button title="View Receipt" className="op-action-btn" onClick={() => window.open(`${UPLOAD_URL}${inv.paymentAttachment}`)}>
                                                <Icon d={icons.eye} size={13} />
                                            </button>
                                        )}
                                        <button title="Delete" className="op-action-btn btn-delete" onClick={() => handleDelete(inv._id)}>
                                            <Icon d={icons.trash} size={13} />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* (legacy modal placeholder — replaced by inline page render above) */}

            {false && modalOpen && (
                <div className="invoice-modal-overlay" onClick={() => setModalOpen(false)}>
                    <div className="invoice-modal-content" onClick={e => e.stopPropagation()}>
                        <div className="invoice-modal-header">
                            <h2>{editItem ? 'Edit Invoice' : 'Create New Invoice'}</h2>
                            <button className="close-btn" onClick={() => setModalOpen(false)}><Icon d={icons.x} size={20} /></button>
                        </div>
                        <form onSubmit={handleSave}>
                            <div className="invoice-modal-body">
                                <div className="invoice-form-grid">
                                    <div className="form-group">
                                        <label>Select Client</label>
                                        <select
                                            required
                                            value={formData.clientId}
                                            onChange={e => setFormData({ ...formData, clientId: e.target.value })}
                                        >
                                            <option value="">Select a Client</option>
                                            {clients.map(c => <option key={c._id} value={c._id}>{c.companyName}</option>)}
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <label>Invoice Number</label>
                                        <input
                                            type="text"
                                            required
                                            value={formData.invoiceNumber}
                                            onChange={e => setFormData({ ...formData, invoiceNumber: e.target.value })}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Issue Date</label>
                                        <input
                                            type="date"
                                            required
                                            value={formData.issueDate}
                                            onChange={e => setFormData({ ...formData, issueDate: e.target.value })}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Due Date</label>
                                        <input
                                            type="date"
                                            required
                                            value={formData.dueDate}
                                            onChange={e => setFormData({ ...formData, dueDate: e.target.value })}
                                        />
                                    </div>
                                    <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                                        <label>Subject</label>
                                        <input
                                            type="text"
                                            placeholder="e.g. Monthly Transportation Services"
                                            value={formData.subject}
                                            onChange={e => setFormData({ ...formData, subject: e.target.value })}
                                        />
                                    </div>
                                    <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                                        <label>Description</label>
                                        <input
                                            type="text"
                                            placeholder="e.g. Services provided for the month of April 2026"
                                            value={formData.description}
                                            onChange={e => setFormData({ ...formData, description: e.target.value })}
                                        />
                                    </div>
                                </div>

                                <div className="invoice-items-section">
                                    <div className="items-header">
                                        <h3>Invoice Items (Routes)</h3>
                                        <button type="button" className="add-item-btn" onClick={handleAddItem}>
                                            <Icon d={icons.plus} size={12} /> Add Route
                                        </button>
                                    </div>
                                    <div className="items-table-wrap">
                                        <table className="items-table">
                                            <thead>
                                                <tr>
                                                    <th>Route</th>
                                                    <th>Trips (Monthly)</th>
                                                    <th>Trips (Daily)</th>
                                                    <th>Amount / Trip</th>
                                                    <th>Total</th>
                                                    <th></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {formData.items.map((item, idx) => (
                                                    <tr key={idx}>
                                                        <td>
                                                            <select
                                                                required
                                                                value={item.routeId}
                                                                onChange={e => handleItemChange(idx, 'routeId', e.target.value)}
                                                            >
                                                                <option value="">Select Route</option>
                                                                {routes.map(r => <option key={r._id} value={r._id}>{r.name}</option>)}
                                                            </select>
                                                        </td>
                                                        <td>
                                                            <input
                                                                type="number"
                                                                value={item.tripsMonthly}
                                                                onChange={e => handleItemChange(idx, 'tripsMonthly', e.target.value)}
                                                            />
                                                        </td>
                                                        <td>
                                                            <input
                                                                type="number"
                                                                value={item.tripsDaily}
                                                                onChange={e => handleItemChange(idx, 'tripsDaily', e.target.value)}
                                                            />
                                                        </td>
                                                        <td>
                                                            <input
                                                                type="number"
                                                                step="0.001"
                                                                value={item.amountPerTrip}
                                                                onChange={e => handleItemChange(idx, 'amountPerTrip', e.target.value)}
                                                            />
                                                        </td>
                                                        <td className="row-total">
                                                            {formatCurrency(item.totalAmount, country)}
                                                        </td>
                                                        <td>
                                                            {formData.items.length > 1 && (
                                                                <button type="button" className="remove-row" onClick={() => handleRemoveItem(idx)}>
                                                                    <Icon d={icons.trash} size={12} />
                                                                </button>
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>

                                <div className="invoice-totals-section">
                                    <div className="totals-title">Additional Charges & Totals</div>
                                    <div className="totals-form-grid">
                                        <div className="form-group">
                                            <label>Additional Amount (Optional)</label>
                                            <input
                                                type="number"
                                                step="0.001"
                                                placeholder="0.000"
                                                value={formData.additionalAmount}
                                                onChange={e => setFormData({ ...formData, additionalAmount: e.target.value })}
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label>VAT (%)</label>
                                            <select
                                                value={formData.vatPercentage}
                                                onChange={e => setFormData({ ...formData, vatPercentage: e.target.value })}
                                            >
                                                <option value="0">0%</option>
                                                <option value="5">5%</option>
                                                <option value="10">10%</option>
                                                <option value="15">15%</option>
                                            </select>
                                        </div>
                                    </div>

                                    <div className="totals-display-box">
                                        <div className="total-row">
                                            <span>Trips Subtotal:</span>
                                            <span>{formatCurrency(tripsSubtotal, country)}</span>
                                        </div>
                                        <div className="total-row">
                                            <span>Additional Amount:</span>
                                            <span>{formatCurrency(Number(formData.additionalAmount) || 0, country)}</span>
                                        </div>
                                        <div className="total-row subtotal-line">
                                            <span>Subtotal:</span>
                                            <span className="bold">{formatCurrency(subtotal, country)}</span>
                                        </div>
                                        <div className="total-row">
                                            <span>VAT ({formData.vatPercentage}%):</span>
                                            <span>{formatCurrency(vatAmount, country)}</span>
                                        </div>
                                        <div className="total-row final-total">
                                            <span>Total Amount:</span>
                                            <span className="grand-total">{formatCurrency(totalAmount, country)}</span>
                                        </div>
                                    </div>
                                </div>

                                <div className="invoice-totals-section" style={{ marginTop: '20px' }}>
                                    <div className="totals-title">Additional Info</div>
                                    <div className="totals-form-grid" style={{ gridTemplateColumns: '1fr' }}>
                                        <div className="form-group">
                                            <label>Notes</label>
                                            <textarea
                                                rows="3"
                                                placeholder="Thank you for your business..."
                                                value={formData.notes}
                                                onChange={e => setFormData({ ...formData, notes: e.target.value })}
                                                style={{ width: '100%', padding: '10px', border: '1px solid #cbd5e1', borderRadius: '4px', fontFamily: 'inherit' }}
                                            ></textarea>
                                        </div>
                                        <div className="form-group">
                                            <label>Signature Name</label>
                                            <input
                                                type="text"
                                                placeholder="Authorized Signatory"
                                                value={formData.signatureName}
                                                onChange={e => setFormData({ ...formData, signatureName: e.target.value })}
                                            />
                                        </div>
                                    </div>
                                </div>

                            </div>
                            <div className="invoice-modal-footer">
                                <button type="button" className="btn-cancel" onClick={() => setModalOpen(false)}>Cancel</button>
                                <button type="submit" className="btn-save">{editItem ? 'Update Invoice' : 'Create Invoice'}</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Status Update Modal */}
            {statusModalOpen && (
                <div className="invoice-modal-overlay" onClick={() => setStatusModalOpen(false)}>
                    <div className="invoice-modal-content small-modal" onClick={e => e.stopPropagation()}>
                        <div className="invoice-modal-header">
                            <h2>Update Invoice Status</h2>
                            <button className="close-btn" onClick={() => setStatusModalOpen(false)}><Icon d={icons.x} size={20} /></button>
                        </div>
                        <form onSubmit={handleUpdateStatus}>
                            <div className="invoice-modal-body">
                                <div className="form-group">
                                    <label>Status</label>
                                    <select
                                        value={paymentData.status}
                                        onChange={e => setPaymentData({ ...paymentData, status: e.target.value })}
                                    >
                                        <option value="Unpaid">Unpaid</option>
                                        <option value="Paid">Paid</option>
                                        <option value="Overdue">Overdue</option>
                                    </select>
                                </div>
                                {paymentData.status === 'Paid' && (
                                    <>
                                        <div className="form-group">
                                            <label>Payment Method</label>
                                            <select
                                                required
                                                value={paymentData.paymentMethod}
                                                onChange={e => setPaymentData({ ...paymentData, paymentMethod: e.target.value })}
                                            >
                                                <option value="">Select Payment Method</option>
                                                {paymentMethods.map(m => (
                                                    <option key={m._id} value={m.name}>{m.name}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="form-group">
                                            <label>Payment Date</label>
                                            <input
                                                type="date"
                                                required
                                                value={paymentData.paidDate}
                                                onChange={e => setPaymentData({ ...paymentData, paidDate: e.target.value })}
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label>Payment Notes</label>
                                            <textarea
                                                rows="3"
                                                placeholder="Enter payment reference or notes..."
                                                value={paymentData.paymentNotes}
                                                onChange={e => setPaymentData({ ...paymentData, paymentNotes: e.target.value })}
                                            ></textarea>
                                        </div>
                                        <div className="form-group">
                                            <label>Proof of Payment (Optional)</label>
                                            <input
                                                type="file"
                                                onChange={e => setPaymentFile(e.target.files[0])}
                                            />
                                        </div>
                                    </>
                                )}
                            </div>
                            <div className="invoice-modal-footer">
                                <button type="button" className="btn-cancel" onClick={() => setStatusModalOpen(false)}>Cancel</button>
                                <button type="submit" className="btn-save">Update Status</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* View Invoice Modal has been moved to a full page return above */}
        </div>
    );
}
